// A cascading select ("Select List (cascading)": Car Models → Toyota → Hilux)
// on a work item, as Jira shapes it (2026-10-27). Pure, so the rules are
// tested without a browser (run-cascading-select-tests).
//
// The value:   { id, value, child?: { id, value } } — parent, then one child.
// The options: allowedValues = the parents, each with `children` (editmeta /
//              createmeta nest them; see cascading-options.js on the server).
// The save:    { id } or { id, child: { id } }; null clears the field.
//
// Before this the item view had no cascading type: the field fell through
// to 'text', printed its stored object as raw JSON, and its editor was a
// text box that would have saved that JSON as the value.

// The schema says option-with-child; a field whose catalogue lost the type
// still carries Jira's custom id.
export const isCascadingSchema = (schema) =>
  schema?.type === 'option-with-child' || /cascadingselect/i.test(String(schema?.custom || ''));

// "Toyota - Hilux", "Toyota" (no child picked), '' (empty). The separator is
// the one ZQL already uses for this field's text (stride-adapter CASCADE), so
// the item view, search and the grids read the same.
export function cascadingLabel(value) {
  if (!value || typeof value !== 'object') return '';
  const parent = value.value ?? value.name ?? '';
  const child = value.child && typeof value.child === 'object' ? (value.child.value ?? value.child.name ?? '') : '';
  if (!parent) return '';
  return child ? `${parent} - ${child}` : String(parent);
}

// The children offered under one parent — none when it has none.
export function cascadingChildren(allowedValues, parentId) {
  if (parentId == null || parentId === '') return [];
  const parent = (allowedValues || []).find((o) => String(o.id) === String(parentId));
  return Array.isArray(parent?.children) ? parent.children : [];
}

// The value the editor holds after a pick, labels included (the panel paints
// it straight away, before the save returns).
export function cascadingPick(allowedValues, parentId, childId) {
  const parent = (allowedValues || []).find((o) => String(o.id) === String(parentId));
  if (!parent) return null;
  const child = cascadingChildren(allowedValues, parentId).find((c) => String(c.id) === String(childId));
  return {
    id: String(parent.id),
    value: parent.value ?? parent.name ?? '',
    ...(child ? { child: { id: String(child.id), value: child.value ?? child.name ?? '' } } : {}),
  };
}

// What the write path is sent: ids only (the server fills the labels).
export function cascadingPayload(value) {
  if (!value || value.id == null || value.id === '') return null;
  const childId = value.child && typeof value.child === 'object' ? value.child.id : null;
  return childId != null && childId !== ''
    ? { id: String(value.id), child: { id: String(childId) } }
    : { id: String(value.id) };
}
